import {
    type ReactNode,
    useEffect,
    useCallback,
    useMemo,
    useState,
    useRef,
} from "react";
import * as React from "react";
import { createClasses } from "@bloomberg/media-ui-core";

import InactiveLeftIcon from "assets/chevron-thick-left-inactive.svg";
import LeftIcon from "assets/chevron-thick-left.svg";
import InactiveRightIcon from "assets/chevron-thick-right-inactive.svg";
import RightIcon from "assets/chevron-thick-right.svg";
import { accessibleOnClick } from "~utils/accessibility";

import styles from "./styles.module.css";

const CAROUSEL_LIMIT = 6;

const MIN_SWIPE_DISTANCE = 50;

type DotProps = {
    onClick: (index: number) => void;
    index: number;
    isActive: boolean;
};

function Dot({ onClick, index, isActive }: DotProps) {
    const handleOnClick = useCallback(() => {
        onClick(index);
    }, [onClick, index]);

    return (
        <span
            className={createClasses([
                styles.dot,
                isActive && styles.activeDot,
            ])}
            aria-label="navigation dot"
            {...accessibleOnClick(handleOnClick)}
        />
    );
}

const NavButtonIcon = ({
    direction,
    active,
}: {
    direction: string;
    active: boolean;
}) => {
    const navButton = createClasses([
        styles.navButtonContainer,
        active ? styles.activeButton : styles.inactiveButton,
    ]);
    return (
        <div className={navButton}>
            {direction === "left" &&
                (active ? <LeftIcon /> : <InactiveLeftIcon />)}
            {direction === "right" &&
                (active ? <RightIcon /> : <InactiveRightIcon />)}
        </div>
    );
};

type CarouselNavProps = {
    children: ReactNode[];
    limit?: number;
    autoadvanceIntervalInMs?: null | number;
    animatedTransition?: boolean;
    trackingName?: string;
    noSpaceBetweenItems?: boolean;
    trackClick?: (eventAction: string, eventLabel: string) => void;
};

export const CarouselNav = ({
    children,
    limit = CAROUSEL_LIMIT,
    autoadvanceIntervalInMs = null,
    animatedTransition = false,
    trackingName = "carousel",
    noSpaceBetweenItems = false,
    trackClick,
}: CarouselNavProps) => {
    const autoadvancing = useRef<NodeJS.Timeout | null>(null);
    const [currentCard, setCurrentCard] = useState(0);
    const touchStart = useRef<number | null>(null);
    const touchEnd = useRef<number | null>(null);
    const navSectionButtonsClass = createClasses([
        styles.navSection,
        styles.navSectionButtons,
    ]);

    const items = useMemo(
        () => children.slice(0, limit + 1),
        [children, limit]
    );
    const autoadvance = useCallback((interval: number, cardCount: number) => {
        autoadvancing.current = setInterval(() => {
            setCurrentCard((prevCard) => (prevCard + 1) % cardCount);
        }, interval);
    }, []);

    const stopAutoadvance = useCallback(() => {
        if (autoadvancing.current) {
            clearInterval(autoadvancing.current);
            autoadvancing.current = null;
        }
    }, []);

    const resetAutoadvance = useCallback(() => {
        stopAutoadvance();

        if (autoadvanceIntervalInMs) {
            autoadvance(autoadvanceIntervalInMs, items.length);
        }
    }, [autoadvanceIntervalInMs, items.length, autoadvance, stopAutoadvance]);

    useEffect(() => {
        if (autoadvanceIntervalInMs) {
            autoadvance(autoadvanceIntervalInMs, items.length);
        }

        return stopAutoadvance;
    }, [autoadvanceIntervalInMs, items.length, autoadvance, stopAutoadvance]);

    const navigateLeft = useCallback(() => {
        setCurrentCard((prevCard) => Math.max(prevCard - 1, 0));

        resetAutoadvance();

        if (trackClick) {
            trackClick("arrow-click", `${trackingName}.arrow-back`);
        }
    }, [resetAutoadvance]);

    const navigateRight = useCallback(() => {
        setCurrentCard((prevCard) => Math.min(prevCard + 1, items.length - 1));

        resetAutoadvance();

        if (trackClick) {
            trackClick("arrow-click", `${trackingName}.arrow-forward`);
        }
    }, [resetAutoadvance, items.length]);

    const navigateToCard = useCallback(
        (nextCurrentCard: number) => {
            setCurrentCard(nextCurrentCard);

            resetAutoadvance();

            if (trackClick) {
                trackClick(
                    "arrow-click",
                    `${trackingName}.card.${nextCurrentCard + 1}`
                );
            }
        },
        [resetAutoadvance]
    );

    const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        touchEnd.current = null;
        touchStart.current = e.targetTouches[0].clientX;
    };

    const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        touchEnd.current = e.targetTouches[0].clientX;
    };

    const onTouchEnd = () => {
        if (touchStart.current === null || touchEnd.current === null) return;

        const distance = touchEnd.current - touchStart.current;
        const swipeLeftToRight = distance > MIN_SWIPE_DISTANCE;
        const swipeRightToLeft = distance < -MIN_SWIPE_DISTANCE;

        if (swipeLeftToRight) {
            navigateLeft();
        }

        if (swipeRightToLeft) {
            navigateRight();
        }
    };

    const isAtStart = currentCard === 0;
    const isAtEnd = currentCard === items.length - 1;

    if (!items.length) {
        return <></>;
    }

    if (animatedTransition) {
        return (
            <>
                <div
                    className={styles.animatedBox}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    {items.map((item, index) => {
                        const cx = createClasses([
                            styles.animatedCard,
                            index < currentCard &&
                                !noSpaceBetweenItems &&
                                styles.prevCard,
                            index < currentCard &&
                                noSpaceBetweenItems &&
                                styles.prevCardNoSpace,
                            index > currentCard &&
                                !noSpaceBetweenItems &&
                                styles.nextCard,
                            index > currentCard &&
                                noSpaceBetweenItems &&
                                styles.nextCardNoSpace,
                        ]);

                        return (
                            <span className={cx} key={index}>
                                {item}
                            </span>
                        );
                    })}
                </div>
                <div className={styles.carouselNav}>
                    <div className={styles.navSection} />
                    <div className={styles.navSection}>
                        {items.map((_, index) => (
                            <Dot
                                key={index}
                                isActive={index === currentCard}
                                onClick={navigateToCard}
                                index={index}
                            />
                        ))}
                    </div>
                    <div className={navSectionButtonsClass}>
                        <button
                            onClick={navigateLeft}
                            disabled={isAtStart}
                            aria-label="navigate left"
                        >
                            <NavButtonIcon
                                direction="left"
                                active={!isAtStart}
                            />
                        </button>
                        <button
                            onClick={navigateRight}
                            disabled={isAtEnd}
                            aria-label="navigate right"
                        >
                            <NavButtonIcon
                                direction="right"
                                active={!isAtEnd}
                            />
                        </button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            {items[currentCard]}
            <div className={styles.carouselNav}>
                <div className={styles.navSection} />
                <div className={styles.navSection}>
                    {items.map((_, index) => (
                        <Dot
                            key={index}
                            isActive={index === currentCard}
                            onClick={setCurrentCard}
                            index={index}
                        />
                    ))}
                </div>
                <div className={navSectionButtonsClass}>
                    <button
                        onClick={navigateLeft}
                        disabled={isAtStart}
                        aria-label="navigate left"
                    >
                        <NavButtonIcon direction="left" active={!isAtStart} />
                    </button>
                    <button
                        onClick={navigateRight}
                        disabled={isAtEnd}
                        aria-label="navigate right"
                    >
                        <NavButtonIcon direction="right" active={!isAtEnd} />
                    </button>
                </div>
            </div>
        </>
    );
};
